import {
  Component,
  Input,
  OnInit,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { Subscription } from 'rxjs';
import {
  AnalyticsPeriod,
  TangramMetrics,
  MyTangramRow,
  MediaType,
  TrendDelta,
} from '../../models/analytics.types';
import {
  ProfessorAnalyticsService,
  TangramMetricsListCriteria,
} from '../../services/professor-analytics.service';
import { PAGE_SIZE } from '../../models/period-trends.const';

@Component({
  selector: 'tangram-pa-tangram-performance-tab',
  templateUrl: './tangram-performance-tab.component.html',
  styleUrls: ['./tangram-performance-tab.component.scss']
})
export class TangramPerformanceTabComponent implements OnInit, OnChanges {
  // Period flows down from the parent so the dashboard's period dropdown
  // refetches every tab in lockstep.
  @Input() period: AnalyticsPeriod = 'This Month';
  @Input() startDate = '';
  @Input() endDate   = '';

  loading = true;
  // Separate flag for re-fetches caused by pagination — the table swaps
  // its rows on a smaller indicator, the tab as a whole stays mounted.
  pageLoading = false;
  error = '';

  tangramMetrics: TangramMetrics = { views: 0, uniqueUsers: 0, completionRate: 0, avgTimeSpent: '0m' };
  myTangrams: MyTangramRow[] = [];
  myTangramsTotal = 0;
  // Server-computed (top/low by completion rate, gated by min-views) so the
  // tab does not second-guess what counts as "top" or "lowest".
  topTangrams: MyTangramRow[] = [];
  lowTangrams: MyTangramRow[] = [];
  trends: { [key: string]: TrendDelta } = {};
  criteria: TangramMetricsListCriteria = { minViewsForRanking: 5, listSize: 5 };

  readonly PAGE_SIZE = PAGE_SIZE;
  myTangramPage = 1;

  private fetchSub?: Subscription;

  constructor(private analyticsService: ProfessorAnalyticsService) {}

  ngOnInit(): void {
    this.load();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['period'] || changes['startDate'] || changes['endDate']) &&
        !(changes['period'] && changes['period'].firstChange)) {
      // New period → reset to page 1; the row set is brand-new and the
      // old page index is meaningless against the new data.
      this.myTangramPage = 1;
      this.load();
    }
  }

  // Pagination component emits the new page number — re-fetch that slice
  // from the server. We don't need to recompute KPIs / trends / top-low
  // (they don't depend on the page), but the existing endpoint returns
  // them anyway and re-assigning identical values is a no-op visually.
  onMyTangramPageChange(page: number): void {
    if (page === this.myTangramPage) { return; }
    this.myTangramPage = page;
    this.load();
  }

  trend(key: string): TrendDelta {
    return this.trends[key] || { delta: 0, unit: '' };
  }

  // Display label for each media-type chip. Backend emits the lower-case
  // tokens — we capitalise here so the chip reads "PDF" / "Video" / etc.
  // rather than the raw token.
  mediaTypeLabel(type: MediaType): string {
    const labels: { [k in MediaType]: string } = {
      video: 'Video',
      audio: 'Audio',
      pdf: 'PDF',
      image: 'Image',
      document: 'Document',
      article: 'Article',
    };
    return labels[type] || type;
  }

  private load(): void {
    if (this.fetchSub) {
      this.fetchSub.unsubscribe();
    }
    // First load shows the tab-level skeleton; subsequent page changes
    // only swap the table rows, so the rest of the tab stays painted.
    const isFirstLoad = this.myTangrams.length === 0 && this.myTangramsTotal === 0;
    if (isFirstLoad) { this.loading = true; } else { this.pageLoading = true; }
    this.error = '';

    this.fetchSub = this.analyticsService.getTangramMetricsAnalytics(
      this.period,
      this.startDate,
      this.endDate,
      this.myTangramPage,
      this.PAGE_SIZE,
    ).subscribe(
      (data) => {
        this.tangramMetrics = data.tangramMetrics;
        // Server now returns just the page slice plus the full count.
        this.myTangrams = data.myTangrams || [];
        this.myTangramsTotal = typeof data.myTangramsTotal === 'number'
          ? data.myTangramsTotal
          : this.myTangrams.length;
        this.topTangrams = data.topTangrams || [];
        this.lowTangrams = data.lowTangrams || [];
        this.trends = data.trends || {};
        if (data.criteria) {
          this.criteria = data.criteria;
        }
        // Publish to the per-tab export cache (see service comment).
        this.analyticsService.setLatestTabData('tangram-performance', data);
        this.loading = false;
        this.pageLoading = false;
      },
      (err) => {
        this.error = (err && err.message) ? err.message : 'Failed to load Tangram metrics.';
        this.loading = false;
        this.pageLoading = false;
      },
    );
  }
}
